import React, { useEffect, useState, useCallback } from "react";
import { Wallet, Banknote, Heart, Ticket, ArrowUpRight, CheckCircle2, Loader2, Target, History, Link2, Share2, QrCode, Mic, Video, Truck, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "@/context/AuthContext";
import TipGoalBar from "@/components/TipGoalBar";

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

const GOAL_TEMPLATES = [
  { slug: "mic", label: "New mic", amount: 250, icon: Mic },
  { slug: "studio", label: "Studio time", amount: 500, icon: Clock },
  { slug: "video", label: "Music video", amount: 1000, icon: Video },
  { slug: "tour", label: "Tour van", amount: 2500, icon: Truck },
];

export default function Earnings() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [goalAmount, setGoalAmount] = useState("");
  const [goalLabel, setGoalLabel] = useState("");
  const [goalDeadline, setGoalDeadline] = useState("");
  const [savingGoal, setSavingGoal] = useState(false);
  const [posting, setPosting] = useState(false);
  const [showQR, setShowQR] = useState(false);

  const shareUrl = user ? `${window.location.origin}/tip/${user.id}` : "";

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Share link copied!"); }
    catch { toast.error("Could not copy — link: " + shareUrl); }
  };

  const postToFeed = async () => {
    setPosting(true);
    try {
      await api.post("/posts", { text: `Help me hit my tip goal${data?.tip_goal_label ? `: ${data.tip_goal_label}` : ""} 🎯 Every tip counts!`, tip_goal: true });
      toast.success("Posted to your feed!");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not post"); }
    finally { setPosting(false); }
  };

  const load = useCallback(() => {
    api.get("/connect/status").then(({ data }) => setStatus(data)).catch(() => {});
    api.get("/earnings/me").then(({ data }) => {
      setData(data);
      setGoalAmount(data.tip_goal_cents ? String(data.tip_goal_cents / 100) : "");
      setGoalLabel(data.tip_goal_label || "");
      setGoalDeadline(data.tip_goal_deadline ? data.tip_goal_deadline.slice(0, 10) : "");
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const onboard = async () => {
    setBusy(true);
    try { const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start setup"); setBusy(false); }
  };

  const withdraw = async () => {
    setBusy(true);
    try { const { data } = await api.post("/connect/payout"); toast.success(`Payout of ${money(data.amount_cents)} initiated`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Payout failed"); } finally { setBusy(false); }
  };

  const saveGoal = async (override) => {
    const amt = override && typeof override.amount === "number" ? override.amount : Number(goalAmount) || 0;
    const lbl = override && typeof override.label === "string" ? override.label : goalLabel;
    const dl = override && "deadline" in override ? override.deadline : (goalDeadline || null);
    setSavingGoal(true);
    try {
      await api.put("/tip-goal", { amount: amt, label: lbl, deadline: dl });
      toast.success(amt > 0 ? "Tip goal updated" : "Tip goal cleared");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save goal"); }
    finally { setSavingGoal(false); }
  };

  const applyTemplate = (t) => {
    setGoalAmount(String(t.amount));
    setGoalLabel(t.label);
    saveGoal({ amount: t.amount, label: t.label });
  };

  const ready = status?.charges_enabled;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6" data-testid="earnings-page">
      <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">Your <span className="text-gradient">Earnings</span></h1>
      <p className="text-zinc-400 mt-2">Fans support you with tips and party tickets. You keep 90% — NEXT UP takes 10%.</p>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="nu-card p-6 sm:col-span-1">
          <div className="flex items-center gap-2 text-zinc-400 text-sm"><Wallet className="w-4 h-4 text-emerald-400" /> Balance</div>
          <p className="mt-2 font-display font-black text-4xl" data-testid="earnings-balance">{money(data?.balance_cents)}</p>
        </div>
        <div className="nu-card p-6 flex items-center gap-3"><Heart className="w-6 h-6 text-red-400" /><div><p className="font-mono-nu text-2xl">{data?.tips ?? 0}</p><p className="text-xs text-zinc-500">tips received</p></div></div>
        <div className="nu-card p-6 flex items-center gap-3"><Ticket className="w-6 h-6 text-violet-400" /><div><p className="font-mono-nu text-2xl">{data?.tickets ?? 0}</p><p className="text-xs text-zinc-500">tickets sold</p></div></div>
      </div>

      {/* Tip Goal */}
      <div className="mt-6 nu-card p-6">
        <h2 className="font-bold text-lg mb-1 flex items-center gap-2"><Target className="w-5 h-5 text-emerald-400" /> Monthly Tip Goal</h2>
        <p className="text-zinc-400 text-sm mb-4">Set a target and fans can rally to fill your bar. Resets on the 1st of each month.</p>

        <div className="mb-4">
          <p className="text-xs text-zinc-500 mb-2">Quick start — one tap</p>
          <div className="flex flex-wrap gap-2" data-testid="goal-templates">
            {GOAL_TEMPLATES.map((t) => (
              <button key={t.label} onClick={() => applyTemplate(t)} disabled={savingGoal} data-testid={`goal-template-${t.slug}`}
                className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-black/40 border border-white/10 hover:border-emerald-500/60 text-sm font-semibold transition-colors disabled:opacity-50">
                <t.icon className="w-4 h-4 text-emerald-400" /> {t.label} <span className="text-zinc-500 font-mono-nu">${t.amount}</span>
              </button>
            ))}
          </div>
        </div>

        {data?.tip_goal_cents > 0 && (
          <div className="mb-4">
            <TipGoalBar goalCents={data.tip_goal_cents} progressCents={data.tip_progress_cents} label={data.tip_goal_label} deadline={data.tip_goal_deadline} compact />
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[140px_1fr_170px_auto] gap-3 items-end">
          <div>
            <label className="text-xs text-zinc-400">Goal (USD)</label>
            <input type="number" min="0" value={goalAmount} onChange={(e) => setGoalAmount(e.target.value)} data-testid="tip-goal-amount-input"
              placeholder="0 = none" className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 outline-none focus:border-emerald-500/50 mt-1" />
          </div>
          <div>
            <label className="text-xs text-zinc-400">Label (optional)</label>
            <input type="text" maxLength={80} value={goalLabel} onChange={(e) => setGoalLabel(e.target.value)} data-testid="tip-goal-label-input"
              placeholder="e.g. New studio mic fund" className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 outline-none focus:border-emerald-500/50 mt-1" />
          </div>
          <div>
            <label className="text-xs text-zinc-400">Deadline (optional)</label>
            <input type="date" value={goalDeadline} onChange={(e) => setGoalDeadline(e.target.value)} data-testid="tip-goal-deadline-input"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 outline-none focus:border-emerald-500/50 mt-1 text-zinc-200" />
          </div>
          <button onClick={() => saveGoal()} disabled={savingGoal} data-testid="save-tip-goal-btn"
            className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold text-sm disabled:opacity-50 h-[46px]">
            {savingGoal ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Goal"}
          </button>
        </div>

        {data?.tip_goal_cents > 0 && (
          <div className="mt-5 pt-4 border-t border-white/5">
            <p className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-2"><Share2 className="w-4 h-4 text-emerald-400" /> Share your goal so fans can rally</p>
            <div className="flex flex-wrap items-center gap-3">
              <button onClick={copyLink} data-testid="copy-tip-link-btn"
                className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 hover:border-emerald-500/60 font-semibold text-sm transition-colors">
                <Link2 className="w-4 h-4" /> Copy share link
              </button>
              <button onClick={postToFeed} disabled={posting} data-testid="post-tip-goal-btn"
                className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold text-sm disabled:opacity-50 transition-colors">
                {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Post to feed
              </button>
              <button onClick={() => setShowQR((v) => !v)} data-testid="toggle-qr-btn"
                className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 hover:border-emerald-500/60 font-semibold text-sm transition-colors">
                <QrCode className="w-4 h-4" /> {showQR ? "Hide QR" : "Show QR"}
              </button>
              <span className="text-xs text-zinc-500 font-mono-nu truncate max-w-full">{shareUrl}</span>
            </div>
            {showQR && (
              <div className="mt-4 flex flex-col sm:flex-row items-center gap-4" data-testid="tip-qr">
                <div className="bg-white p-3 rounded-2xl shrink-0">
                  <QRCodeSVG value={shareUrl} size={160} level="M" includeMargin={false} />
                </div>
                <p className="text-sm text-zinc-400 max-w-xs">Flash this at your shows or drop it on a story — fans scan it to tip you instantly, no account needed.</p>
              </div>
            )}
            <p className="mt-2 text-xs text-zinc-500">Anyone with the link can tip you — no account needed.</p>
          </div>
        )}
      </div>

      {/* Payouts */}
      <div className="mt-6 nu-card p-6">
        <h2 className="font-bold text-lg mb-3">Payouts</h2>
        {!status ? <Loader2 className="w-5 h-5 animate-spin text-emerald-400" /> : ready ? (
          <div className="flex flex-wrap items-center gap-3">
            <span className="flex items-center gap-2 text-emerald-400 text-sm"><CheckCircle2 className="w-4 h-4" /> Bank payouts enabled</span>
            <button onClick={withdraw} disabled={busy} data-testid="withdraw-btn"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold text-sm disabled:opacity-50">
              <Banknote className="w-4 h-4" /> Withdraw to bank
            </button>
            <button onClick={onboard} className="text-xs text-zinc-400 hover:text-white underline">Update bank details</button>
          </div>
        ) : (
          <div>
            <p className="text-zinc-400 text-sm mb-3">Connect your bank with Stripe to receive tips and ticket sales.</p>
            <button onClick={onboard} disabled={busy} data-testid="connect-onboard-btn"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold text-sm disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />} Set up payouts
            </button>
          </div>
        )}
      </div>

      {/* Payout History */}
      <div className="mt-6 nu-card p-6">
        <h2 className="font-bold text-lg mb-4 flex items-center gap-2"><History className="w-5 h-5 text-emerald-400" /> Payout History</h2>
        {(!data || (data.payouts || []).length === 0) ? <p className="text-zinc-500 text-sm">No withdrawals yet.</p> : (
          <div className="space-y-2" data-testid="payout-history">
            {data.payouts.map((p) => (
              <div key={p.id} className="flex items-center justify-between py-2 border-b border-white/5">
                <span className="flex items-center gap-2 text-sm text-zinc-300">
                  <Banknote className="w-4 h-4 text-emerald-400" /> {fmtDate(p.created_at)}
                  <span className="text-xs text-zinc-600 capitalize">· {p.status}</span>
                </span>
                <span className="font-mono-nu text-white">-{money(p.amount_cents)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent activity */}
      <div className="mt-6 nu-card p-6">
        <h2 className="font-bold text-lg mb-4">Recent activity</h2>
        {(!data || data.entries.length === 0) ? <p className="text-zinc-500 text-sm">No earnings yet. Share your profile and go live!</p> : (
          <div className="space-y-2" data-testid="earnings-ledger">
            {data.entries.map((e) => (
              <div key={e.id} className="flex items-center justify-between py-2 border-b border-white/5">
                <span className="flex items-center gap-2 text-sm">
                  {e.type === "tip" ? <Heart className="w-4 h-4 text-red-400" /> : <Ticket className="w-4 h-4 text-violet-400" />}
                  {e.type === "tip" ? "Tip" : "Party ticket"}
                </span>
                <span className="font-mono-nu text-emerald-400">+{money(e.net)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
